/**
 * Centralized configuration for the video overlay system
 * This makes it easy to adjust styling, positioning, and behavior
 * without modifying multiple components
 */

export const VIDEO_CONFIG = {
  // Frame dimensions. Informational only: the live values are the
  // `--video-tile-*` custom properties in `index.css` (#106).
  layout: {
    frameHeight: {
      base: 'min(24vh, 16vw)', // Preferred height; the vw cap keeps two across
      min: '140px',            // Minimum height for readability
      max: '280px'             // Maximum height to prevent oversizing
    },
    aspectRatio: 1.777,   // 16:9 aspect ratio (16/9)
  },

  // Styling configuration
  styles: {
    // Border styles
    localBorder: 'border-gray-400',      // Light gray for local participant
    remoteBorder: 'border-gray-700',     // Dark gray for remote participants
    borderWidth: 'border-4',             // Border thickness

    // Background and text
    noVideoBackground: 'bg-gray-900',    // Dark background for "no video" state
    noVideoText: 'text-gray-500',        // Text color for "no video" message

    // Container styles
    roundedCorners: 'rounded-xl',        // Corner radius
    shadow: 'shadow-lg',                 // Shadow effect

    // Z-index. The app's scale is z-[1000] (game UI layer) / z-[2000]
    // (toasts, chat and customize drawers) / z-[2001] (bottom bar, drawer
    // dialogs) / z-[2002] (popovers) / z-[9999] (modals).
    //
    // Video sits just *under* the drawers (#106): anything the user opens on
    // purpose must win, and at z-[2001] the tiles covered the customize-island
    // drawer's buttons. It is still well above the z-50 it had before #80.
    // Toasts no longer share a corner with the tiles (#131), so the two do
    // not meet.
    zIndex: 'z-[1990]',                  // Overlay z-index
  },

  /**
   * Where the video surfaces sit on screen.
   *
   * The tiles are one row docked at the top of the screen (#131), centred in
   * the width that no drawer covers: `stack` spans the viewport, and
   * {@link getStackPositionClass} narrows it by the chat drawer on the left
   * and the customize drawer on the right while either is open. Below `md`
   * both drawers are full width and cover everything, so there is nothing to
   * narrow. The status panels and the error fallbacks use the same strip, so
   * they appear where the tiles do. The stack no longer shares a corner with
   * the toasts, which used to push it 80px down: toasts live in the
   * lower-right corner (see `Notifications` and `DESIGN_SYSTEM.md`).
   *
   * The autoplay-unblock control is the only way audio works when the browser
   * blocks autoplay. It sits one step above the tiles, so nothing video-side
   * can cover it, and one step below the drawers: the customize drawer runs
   * down the whole right edge and would otherwise have a button floating over
   * its own controls. It clears the 4rem bottom bar (z-[2001]) by position,
   * not by stacking, so it is reachable whenever no drawer is open.
   *
   * Toasts never cover it: while it is showing they start at
   * `toastsAboveAudioUnblock` instead of just above the bottom bar. The
   * control is a 2.5rem `Button` at 5rem, so 9rem leaves a clear gap. Keep
   * the two values together.
   */
  position: {
    stack: 'fixed top-4 inset-x-0 flex justify-center',
    stackBesideChatDrawer: 'md:left-[600px]',          // ChatDrawer is md:w-[600px]
    stackBesideDecorationsDrawer: 'md:right-[400px]',  // DecorationDrawer is md:w-[400px]
    audioUnblock: 'fixed bottom-20 right-4',
    audioUnblockZIndex: 'z-[1991]',
    toastsAboveAudioUnblock: 'bottom-36',
  },

  /**
   * Value for the `data-lk-theme` attribute on video roots.
   *
   * Deliberately not `default`: that token would match the vendor stylesheet's
   * own palette block and override the game-palette `--lk-*` tokens defined in
   * `index.css`. Any other value still matches LiveKit's unqualified
   * `[data-lk-theme]` base rules.
   */
  theme: 'minka',

  /**
   * Speaking ring (issue #103), driven by the client-side audio level of each
   * participant's microphone rather than LiveKit's server-side `isSpeaking`,
   * which arrives late.
   *
   * `analyser` is handed to `useTrackVolume`, which samples it at 30 Hz. The
   * library default maps -100..-80 dB onto the whole 0..1 range, so ordinary
   * room noise would saturate it; this range leaves headroom for speech.
   * `threshold` is on that 0..1 scale. `holdMs` keeps the ring lit through
   * the gaps between syllables and words so it does not flicker.
   *
   * These are starting values that need tuning by ear on real calls.
   */
  speaking: {
    threshold: 0.15,
    holdMs: 400,
    fadeInMs: 120,     // Ring fade in: quick, so it still reads as immediate
    fadeOutMs: 450,    // Ring fade out: slow, so pauses between words do not blink it
    analyser: {
      fftSize: 32,
      smoothingTimeConstant: 0,
      minDecibels: -90,
      maxDecibels: -30,
    },
  },

  // Messages shown in different states
  messages: {
    noVideo: 'No video',
    waiting: 'Waiting...',
    connecting: 'Connecting...',
  },

  // Auto-connection settings
  autoConnect: {
    delay: 300,           // Delay before auto-connecting (ms)
    conditions: {
      requiresMediaEnabled: true,  // Require camera or mic to be enabled
      respectDND: true,            // Don't connect when in DND mode
      autoJoinDefault: true,       // Default autoJoin preference
    },
  },

  // Animation settings
  animations: {
    transitionDuration: 500,      // Duration of position transitions (ms)
    transitionEasing: 'ease-in-out',
  },

  // Error and reconnection settings
  errors: {
    maxReconnectAttempts: 3,
    reconnectDelay: 2000,
  },
} as const;

// Helper to determine border style based on participant type
export function getFrameBorderStyle(isLocal: boolean): string {
  return isLocal ? VIDEO_CONFIG.styles.localBorder : VIDEO_CONFIG.styles.remoteBorder;
}

/**
 * The strip the tile row is centred in: the whole viewport width, less
 * whichever drawers are open (#131). See `VIDEO_CONFIG.position`.
 */
export function getStackPositionClass(openDrawers: { chat: boolean; decorations: boolean }): string {
  const { stack, stackBesideChatDrawer, stackBesideDecorationsDrawer } = VIDEO_CONFIG.position;
  return [
    stack,
    openDrawers.chat ? stackBesideChatDrawer : '',
    openDrawers.decorations ? stackBesideDecorationsDrawer : ''
  ].filter(Boolean).join(' ');
}